import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

const RAW_BASE_URL =
  process.env['NX_PUBLIC_API_BASE_URL'] || 'http://localhost:8080';
const BASE_URL = RAW_BASE_URL.endsWith('/')
  ? RAW_BASE_URL.slice(0, -1)
  : RAW_BASE_URL;

// --- Types ---

interface GmailStatus {
  connected: boolean;
  gmail_address: string | null;
  watch_expiry: string | null;
}

interface GmailConnectResponse {
  authorize_url: string;
}

// --- Query key factory ---

export const gmailKeys = {
  all: ['gmail'] as const,
  status: () => [...gmailKeys.all, 'status'] as const,
};

// --- Query hooks ---

export function useGmailStatus() {
  return useQuery<GmailStatus>({
    queryKey: gmailKeys.status(),
    queryFn: async () => {
      const response = await axios.get(`${BASE_URL}/internal/gmail/status`, {
        withCredentials: true,
      });
      return response.data;
    },
    refetchOnWindowFocus: true,
    staleTime: 1000 * 30, // 30 seconds
  });
}

// --- Mutation hooks ---

export function useGmailDisconnect() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await axios.post(
        `${BASE_URL}/internal/gmail/disconnect`,
        {},
        { withCredentials: true },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gmailKeys.status() });
    },
  });
}

export function useGmailTest() {
  return useMutation({
    mutationFn: async () => {
      const response = await axios.post(
        `${BASE_URL}/internal/gmail/test`,
        {},
        { withCredentials: true },
      );
      return response.data;
    },
  });
}

// --- Connect helper ---

export async function gmailConnect() {
  const response = await axios.get<GmailConnectResponse>(
    `${BASE_URL}/internal/gmail/connect`,
    { withCredentials: true },
  );
  window.location.href = response.data.authorize_url;
}
